/**
 * Sidebar — 72px fixed left nav with 7 room items + settings.
 * Active state driven by React Router location.
 * Badges for Decisions (pending count) and Legal (open tickets).
 *
 * Keyboard: g+n/d/a/p/m/c/l (handled in useShortcuts, not here).
 */
import { useNavigate, useLocation } from "react-router-dom";
import { Radar, Inbox, Cpu, Users, Coins, Compass, Scale, Settings } from "lucide-react";

const ROOMS = [
  { id: "now",       label: "Now",       icon: Radar,    path: "/mission-control/now"       },
  { id: "decisions", label: "Decisions", icon: Inbox,    path: "/mission-control/decisions", badge: "decisions" },
  { id: "agents",    label: "Agents",    icon: Cpu,      path: "/mission-control/agents"    },
  { id: "people",    label: "People",    icon: Users,    path: "/mission-control/people"    },
  { id: "money",     label: "Money",     icon: Coins,    path: "/mission-control/money"     },
  { id: "compass",   label: "Compass",   icon: Compass,  path: "/mission-control/compass"   },
  { id: "legal",     label: "Legal",     icon: Scale,    path: "/mission-control/legal",    badge: "legal" },
];

export default function Sidebar({ pendingDecisions = 0, openLegalTickets = 0 }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  function getBadge(room) {
    if (room.badge === "decisions" && pendingDecisions > 0) return pendingDecisions;
    if (room.badge === "legal" && openLegalTickets > 0) return openLegalTickets;
    return null;
  }

  return (
    <nav className="mc-sidebar" aria-label="Mission Control rooms">

      {/* ── Xpert logo mark ── */}
      <div className="mc-sidebar-logo" aria-label="Xpert">
        <span className="mc-logo-mark" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M5 12.5 L10 17.5 L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      <ul className="mc-sidebar-list">
        {ROOMS.map((room) => {
          const Icon = room.icon;
          const isActive = pathname === room.path || pathname.startsWith(room.path + "/");
          const badge = getBadge(room);

          return (
            <li key={room.id}>
              <button
                className={`mc-sidebar-item${isActive ? " mc-sidebar-item--active" : ""}`}
                onClick={() => navigate(room.path)}
                aria-label={room.label}
                aria-current={isActive ? "page" : undefined}
                title={room.label}
              >
                <span className="mc-sidebar-icon-wrap">
                  <Icon size={20} aria-hidden="true" />
                  {badge !== null && (
                    <span className="mc-sidebar-badge" aria-label={`${badge} pending`}>
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                </span>
                <span className="mc-sidebar-label">{room.label}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mc-sidebar-bottom">
        <button
          className="mc-sidebar-item"
          onClick={() => window.dispatchEvent(new CustomEvent("mc:openSettings"))}
          aria-label="Settings"
          title="Settings (display, notifications, policies)"
        >
          <span className="mc-sidebar-icon-wrap">
            <Settings size={20} aria-hidden="true" />
          </span>
          <span className="mc-sidebar-label">Settings</span>
        </button>
      </div>
    </nav>
  );
}
